import React, { useState, useMemo } from 'react';

// Master Lifecycle Stages Definition (Active Pre-Order 1_x, Fabric Sourcing 3_x, Factory Production & Shipment 4_x)
const LIFECYCLE_STAGES = [
  // Phase 1: Pre-Order Routing (1_x)
  { seq: 1, code: '1_1', name: 'TECPAC', phase: 'Pre-Order', captain: 'MERCH', source: 'Manual Entry', type: 'garment', trigger: 'Buyer technical pack received with sketch, stitch guidelines, and construction specs.' },
  { seq: 2, code: '1_2', name: 'SPECS', phase: 'Pre-Order', captain: 'MERCH', source: 'Manual Entry', type: 'garment', trigger: 'Measurement charts and graded dimensional specs uploaded and verified.' },
  { seq: 3, code: '1_3', name: 'BOM', phase: 'Pre-Order', captain: 'MERCH', source: 'Manual Entry', type: 'garment', trigger: 'Bill of Materials (Shell, Lining, Trims, Thread, Labels, Polybags) drafted and locked.' },
  { seq: 4, code: '1_4', name: 'SIZE RATIO', phase: 'Pre-Order', captain: 'MERCH', source: 'Manual Entry', type: 'garment', trigger: 'Buyer order size breakdown (S, M, L, XL, XXL) and color-wise ratio confirmed.' },
  { seq: 5, code: '1_5', name: 'PACKING INFO / BARCODE', phase: 'Pre-Order', captain: 'MERCH', source: 'Manual Entry', type: 'garment', trigger: 'Packing method, carton dimensions, and barcode stickers confirmed with Buyer.' },

  // Phase 2: Fabric Sourcing & Mill Execution (3_x)
  { seq: 6, code: '3_1', name: 'FAB PO', phase: 'Fabric Sourcing', captain: 'FAB', source: 'ERP PO', type: 'fabric', trigger: 'Fabric Purchase Order issued to Mill (Form 10375 / 10378).' },
  { seq: 7, code: '3_2', name: 'FAB PI', phase: 'Fabric Sourcing', captain: 'FAB', source: 'Manual Entry', type: 'fabric', trigger: 'Proforma Invoice received from Mill with yarn count, GSM, and payment terms.' },
  { seq: 8, code: '3_3', name: 'HANDLOOM', phase: 'Fabric Sourcing', captain: 'FAB', source: 'Manual Entry', type: 'fabric', trigger: 'Handloom/deskloom weave swatch or print strike-off requested from mill.' },
  { seq: 9, code: '3_4', name: 'HANDLOOM APPROVAL', phase: 'Fabric Sourcing', captain: 'MERCH', source: 'Manual Entry', type: 'fabric', trigger: 'Deskloom pattern/color repeat approved by buyer or lead merchandiser.' },
  { seq: 10, code: '3_5', name: 'LABDIP', phase: 'Fabric Sourcing', captain: 'FAB', source: 'Manual Entry', type: 'fabric', trigger: 'Pantone / color standard submitted to dye house for 3-option lab dip.' },
  { seq: 11, code: '3_6', name: 'LAB DIP APPROVAL', phase: 'Fabric Sourcing', captain: 'MERCH', source: 'Manual Entry', type: 'fabric', trigger: 'Lab dip shade swatch approved under standard light box (D65/TL84/CWF).' },
  { seq: 12, code: '3_7', name: 'WEAVING', phase: 'Fabric Sourcing', captain: 'FAB', source: 'Manual Entry', type: 'fabric', trigger: 'Mill reports loom beam setting and greige weaving commencement.' },
  { seq: 13, code: '3_8', name: 'DYEING', phase: 'Fabric Sourcing', captain: 'FAB', source: 'Manual Entry', type: 'fabric', trigger: 'Greige fabric enters continuous dyeing range or soft-flow vessels.' },
  { seq: 14, code: '3_9', name: 'IND PSS', phase: 'Fabric Sourcing', captain: 'FAB', source: 'Manual Entry', type: 'fabric', trigger: 'India Pre-Shipment Sample (PSS) cut from bulk rolls for physical & shade inspection.' },
  { seq: 15, code: '3_10', name: 'BD PSS', phase: 'Fabric Sourcing', captain: 'SBD', source: 'Manual Entry', type: 'fabric', trigger: 'Bulk PSS received at Bangladesh office for review & customer confirmation.' },
  { seq: 16, code: '3_11', name: 'IND PSS APPROVAL', phase: 'Fabric Sourcing', captain: 'MERCH', source: 'Manual Entry', type: 'fabric', trigger: 'Bulk PSS shade and handfeel signed off; mill authorized to pack rolls.' },
  { seq: 17, code: '3_12', name: 'FPT (FABRIC PERFORMANCE TEST)', phase: 'Fabric Sourcing', captain: 'FAB / MERCH', source: 'Manual Entry', type: 'fabric', trigger: 'Lab test report passes for tear strength, pilling, shrinkage, and color fastness.' },
  { seq: 18, code: '3_13', name: 'FABRIC LC FROM BD TO CHINA', phase: 'Fabric Sourcing', captain: 'COMM', source: 'Manual Entry', type: 'fabric', trigger: 'Commercial bank issues Letter of Credit to Mill for bulk dispatch clearance.' },
  { seq: 19, code: '3_14', name: 'FAB ETD', phase: 'Fabric Sourcing', captain: 'LOG', source: 'Manual Entry', type: 'fabric', trigger: 'Mill dispatches fabric; Estimated Time of Departure from mill port/depot set.' },
  { seq: 20, code: '3_15', name: 'FAB ETA', phase: 'Fabric Sourcing', captain: 'LOG', source: 'Manual Entry', type: 'fabric', trigger: 'Estimated Time of Arrival at destination garment factory port/CFS.' },
  { seq: 21, code: '3_16', name: 'FAB SHIPMENT DOC SHARING', phase: 'Fabric Sourcing', captain: 'LOG', source: 'Manual Entry', type: 'fabric', trigger: 'BL, Commercial Invoice, Packing List shared with factory C&F agent.' },

  // Phase 3: Factory Floor, Trims & Production (4_1 - 4_19)
  { seq: 22, code: '4_1', name: 'ORDER SHARING WITH FACTORY', phase: 'Factory Floor', captain: 'MERCH', source: 'Manual Entry', type: 'garment', trigger: 'Complete technical and trims package formally handed over to factory floor.' },
  { seq: 23, code: '4_2', name: 'GARMENT SALES CONTRACT (BD)', phase: 'Factory Floor', captain: 'COMM', source: 'Manual Entry', type: 'garment', trigger: 'Formal garment manufacturing contract locked with garment factory.' },
  { seq: 24, code: '4_3', name: 'DELIVERY CONFIRM & PRICE CLOSURE', phase: 'Factory Floor', captain: 'COMM / SBD', source: 'Manual Entry', type: 'garment', trigger: 'Commercial closure on CM, agreed factory delivery schedule, and margins.' },
  { seq: 25, code: '4_9', name: 'FAB INHOUSE', phase: 'Factory Floor', captain: 'FACT', source: 'Factory Fab Inhouse', type: 'garment', trigger: 'Bulk rolls received at garment factory stores; 4-point inspection verified.' },
  { seq: 26, code: '4_10', name: 'CHINA TRIMS ETD', phase: 'Factory Floor', captain: 'LOG', source: 'Manual Entry', type: 'garment', trigger: 'Imported accessories (zippers, interlinings) departed overseas supplier.' },
  { seq: 27, code: '4_11', name: 'CHINA TRIMS ETA', phase: 'Factory Floor', captain: 'LOG', source: 'Manual Entry', type: 'garment', trigger: 'Imported trims arrive at port/customs.' },
  { seq: 28, code: '4_12', name: 'CHINA TRIMS IN HOUSE', phase: 'Factory Floor', captain: 'FACT', source: 'Manual Entry', type: 'garment', trigger: 'Overseas buttons, zippers, and care labels received in factory warehouse.' },
  { seq: 29, code: '4_13', name: 'INDIA TRIMS ETD', phase: 'Factory Floor', captain: 'LOG', source: 'Manual Entry', type: 'garment', trigger: 'Domestic trims (polybags, cartons, threads) dispatched from supplier warehouse.' },
  { seq: 30, code: '4_14', name: 'INDIA TRIMS ETA', phase: 'Factory Floor', captain: 'LOG', source: 'Manual Entry', type: 'garment', trigger: 'Domestic trims arrive at factory local hub.' },
  { seq: 31, code: '4_15', name: 'INDIA TRIMS IN HOUSE', phase: 'Factory Floor', captain: 'FACT', source: 'Manual Entry', type: 'garment', trigger: 'All domestic trims 100% inhouse; trims checklist cleared.' },
  { seq: 32, code: '4_16', name: 'PCD (PLANNED CUTTING DATE)', phase: 'Factory Floor', captain: 'FACT / SYS', source: 'Auto-Derived DPR (cuts > 0)', type: 'garment', trigger: 'Factory lays fabric and begins marker cutting (inv_produc_det.cuts > 0).' },
  { seq: 33, code: '4_17', name: 'IN LINE (SEWING COMMENCED)', phase: 'Factory Floor', captain: 'FACT / SYS', source: 'Auto-Derived DPR (prodiss > 0)', type: 'garment', trigger: 'Bundles issued to sewing lines (prodiss > 0 and prod < 98% of prodiss).' },
  { seq: 34, code: '4_18', name: 'LINE OUT (FINISHING DONE)', phase: 'Factory Floor', captain: 'FACT / SYS', source: 'Auto-Derived DPR (prod >= 98%)', type: 'garment', trigger: 'Sewing completes >= 98% of cut output (inv_produc_det.prod >= 0.98 * prodiss).' },
  { seq: 35, code: '4_19', name: 'GPT (GARMENT PERFORMANCE TEST)', phase: 'Factory Floor', captain: 'FACT', source: 'Manual Entry', type: 'garment', trigger: 'Finished garment wash test, seam slippage, and dimensional stability cleared.' },

  // Phase 4: Quality Inspection, Final Costing & SAFTA (4_20)
  { seq: 36, code: '4_20', name: 'FI (FINAL INSPECTION)', phase: 'Shipment', captain: 'SBD', source: 'FI Workflow (Approval)', type: 'garment', trigger: 'Factory proposes date -> SBD inspects -> Approved (garmentsales.fi_date established).' },
  { seq: 37, code: '4_20b', name: 'FINAL COSTING APPROVAL', phase: 'Shipment', captain: 'SBD', source: 'Costflow Workflow', type: 'garment', trigger: 'Factory submits final actual consumption -> SBD team verifies and approves.' },
  { seq: 38, code: '4_20c', name: 'SAFTA / HSN WORKING', phase: 'Shipment', captain: 'RW TEAM', source: 'Daily Activity (RW)', type: 'garment', trigger: 'RW team enters final SAFTA / HSN / COO code + attaches certificate. Unlocks factory shipment.' },

  // Phase 5: Factory Departure & Border Logistics (4_21 - 4_23)
  { seq: 39, code: '4_21', name: 'EX FACT (FACTORY DEPARTURE)', phase: 'Shipment', captain: 'LOG', source: 'Manual / Dispatch Entry', type: 'garment', trigger: 'Inspection cleared + SAFTA issued; container loaded and departs factory gate (garmentsales.Ex_Fac_Date).' },
  { seq: 40, code: '4_22', name: 'BCP / ONWARD DISPATCH', phase: 'Shipment', captain: 'LOG', source: 'Manual / BL Entry', type: 'garment', trigger: 'Border Customs Post (BCP) crossed / Bill of Lading (BL) issued (garmentsales.BCP_Dt).' },
  { seq: 41, code: '4_23', name: 'WH DEL / INVOICED', phase: 'Shipment', captain: 'COMM / SYS', source: 'Challan / Invoicing', type: 'garment', trigger: 'Central warehouse delivery / Sales Invoice issued (garmentsales.Invoice_Dt).' }
];

const CAPTAIN_COLORS = {
  MERCH: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6', border: 'rgba(59, 130, 246, 0.3)' },
  FAB: { bg: 'rgba(16, 185, 129, 0.15)', text: '#10b981', border: 'rgba(16, 185, 129, 0.3)' },
  FACT: { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' },
  SBD: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
  LOG: { bg: 'rgba(139, 92, 246, 0.15)', text: '#8b5cf6', border: 'rgba(139, 92, 246, 0.3)' },
  COMM: { bg: 'rgba(236, 72, 153, 0.15)', text: '#ec4899', border: 'rgba(236, 72, 153, 0.3)' },
  'RW TEAM': { bg: 'rgba(2, 132, 199, 0.15)', text: '#0284c7', border: 'rgba(2, 132, 199, 0.35)' },
  'FAB / MERCH': { bg: 'rgba(16, 185, 129, 0.22)', text: '#059669', border: 'rgba(16, 185, 129, 0.45)' },
  'COMM / SBD': { bg: 'rgba(239, 68, 68, 0.22)', text: '#dc2626', border: 'rgba(239, 68, 68, 0.45)' },
  'FACT / SYS': { bg: 'rgba(245, 158, 11, 0.22)', text: '#d97706', border: 'rgba(245, 158, 11, 0.45)' },
  'COMM / SYS': { bg: 'rgba(236, 72, 153, 0.22)', text: '#db2777', border: 'rgba(236, 72, 153, 0.45)' }
};

export default function StageMatrixView({ setView, Header }) {
  const [phaseFilter, setPhaseFilter] = useState('ALL');
  const [captainFilter, setCaptainFilter] = useState('ALL');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    return LIFECYCLE_STAGES.filter(s => {
      if (phaseFilter !== 'ALL' && s.phase !== phaseFilter) return false;
      if (captainFilter !== 'ALL' && !s.captain.includes(captainFilter)) return false;
      if (q) {
        const search = q.toLowerCase();
        return s.code.toLowerCase().includes(search) ||
               s.name.toLowerCase().includes(search) ||
               s.captain.toLowerCase().includes(search) ||
               s.trigger.toLowerCase().includes(search);
      }
      return true;
    });
  }, [phaseFilter, captainFilter, q]);

  const phases = ['ALL', 'Pre-Order', 'Fabric Sourcing', 'Factory Floor', 'Shipment'];
  const captains = ['ALL', 'MERCH', 'FAB', 'FACT', 'SBD', 'RW TEAM', 'LOG', 'COMM'];

  return (
    <div className="screen">
      <Header subtitle="STAGE & CAPTAIN MATRIX" />
      
      {/* Title bar */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('rw')} style={{ background: 'none', border: 'none', color: '#1A4F9C', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 20, color: '#1A4F9C' }}>Stage & Captain Lifecycle Matrix</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Single source of truth for routing, shopfloor actuals, SAFTA clearance, and department ownership</div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: 'var(--text2)', background: 'var(--bg3)', padding: '4px 10px', borderRadius: 6 }}>
          {filtered.length} of {LIFECYCLE_STAGES.length} Stages
        </div>
      </div>

      {/* Visual Workflow Flowchart */}
      <div style={{ padding: '16px 20px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontWeight: 800, fontSize: 12, color: '#1A4F9C', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
          ⚡ End-to-End Operational Lifecycle Flowchart
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
          {[
            { step: '1. PRE-ORDER', sub: '1_1 to 1_5 (MERCH)', color: '#3b82f6', icon: '📋' },
            { step: '2. FABRIC MILL', sub: '3_1 to 3_16 (FAB / LOG)', color: '#10b981', icon: '🧵' },
            { step: '3. SHOPFLOOR', sub: '4_16 to 4_18 (DPR SYS)', color: '#f59e0b', icon: '✂️' },
            { step: '4. FI INSPECT', sub: '4_20 (SBD Approved)', color: '#ef4444', icon: '🔍' },
            { step: '5. FINAL COSTING', sub: '4_20b (SBD Locked)', color: '#b5651d', icon: '📑' },
            { step: '6. SAFTA WORKING', sub: '4_20c (RW Team)', color: '#0284c7', icon: '🛂' },
            { step: '7. EX FACTORY', sub: '4_21 (Shipment Allowed)', color: '#8b5cf6', icon: '🚢' }
          ].map((node, i) => (
            <React.Fragment key={node.step}>
              <div style={{
                flex: '0 0 auto', padding: '10px 14px', borderRadius: 8,
                background: 'var(--bg)', border: `1.5px solid ${node.color}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', minWidth: 135
              }}>
                <div style={{ fontSize: 14 }}>{node.icon}</div>
                <div style={{ fontWeight: 800, fontSize: 11, color: node.color, marginTop: 4 }}>{node.step}</div>
                <div style={{ fontSize: 9.5, color: 'var(--text3)', marginTop: 2 }}>{node.sub}</div>
              </div>
              {i < 6 && <div style={{ color: 'var(--text3)', fontWeight: 800, fontSize: 14 }}>→</div>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Rationale & Logic Banner */}
      <div style={{ padding: '14px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, fontSize: 12 }}>
          <div style={{ background: 'rgba(26, 79, 156, 0.06)', border: '1px solid rgba(26, 79, 156, 0.2)', padding: '10px 14px', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#1A4F9C', marginBottom: 4 }}>⚙️ Centralized Stage & Status Logic</div>
            <div style={{ color: 'var(--text2)', lineHeight: 1.5 }}>
              All screens (<b>Order in Hand</b>, <b>Monthly Shipment Target</b>, <b>TAS Status</b>) use the exact same state machine. A TAS in <code>IN LINE</code> on the shopfloor is reflected universally with zero manual divergence.
            </div>
          </div>
          <div style={{ background: 'rgba(2, 132, 199, 0.08)', border: '1px solid rgba(2, 132, 199, 0.25)', padding: '10px 14px', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#0284c7', marginBottom: 4 }}>🛂 SAFTA & Factory Shipment Gating</div>
            <div style={{ color: 'var(--text2)', lineHeight: 1.5 }}>
              Once SBD approves Final Costing, RW Team enters final SAFTA & HSN working in <b>Daily Activity</b>. Factory is barred from generating Ex-Factory shipments until SAFTA is saved.
            </div>
          </div>
          <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', padding: '10px 14px', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#d97706', marginBottom: 4 }}>📊 Shopfloor Auto-Derivation (DPR)</div>
            <div style={{ color: 'var(--text2)', lineHeight: 1.5 }}>
              Cutting &gt; 0 → <b>PCD (4_16)</b><br />
              Line Input &gt; 0 and Output &lt; 98% → <b>IN LINE (4_17)</b><br />
              Line Output ≥ 98% of Input → <b>LINE OUT (4_18)</b>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search stage code, name, captain, or rule..."
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg)', color: 'var(--text)', fontSize: 13, minWidth: 260
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' }}>Phase:</span>
          {phases.map(p => (
            <button
              key={p}
              onClick={() => setPhaseFilter(p)}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: phaseFilter === p ? '1px solid #1A4F9C' : '1px solid var(--border)',
                background: phaseFilter === p ? '#1A4F9C' : 'var(--bg)',
                color: phaseFilter === p ? '#fff' : 'var(--text2)'
              }}
            >
              {p}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' }}>Captain:</span>
          {captains.map(c => {
            const colors = CAPTAIN_COLORS[c] || { bg: 'var(--bg)', text: 'var(--text)', border: 'var(--border)' };
            const active = captainFilter === c;
            return (
              <button
                key={c}
                onClick={() => setCaptainFilter(c)}
                style={{
                  padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: active ? `2px solid ${colors.text}` : `1px solid ${colors.border}`,
                  background: active ? colors.bg : 'var(--bg)',
                  color: colors.text
                }}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stages Table */}
      <div style={{ padding: '16px 20px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
          <thead>
            <tr style={{ background: 'var(--bg3)', borderBottom: '2px solid var(--border)', color: 'var(--text2)' }}>
              <th style={{ padding: '10px 12px', width: 60 }}>#</th>
              <th style={{ padding: '10px 12px', width: 90 }}>Code</th>
              <th style={{ padding: '10px 12px', width: 230 }}>Stage Name</th>
              <th style={{ padding: '10px 12px', width: 140 }}>Phase</th>
              <th style={{ padding: '10px 12px', width: 130 }}>Captain</th>
              <th style={{ padding: '10px 12px', width: 210 }}>Update Source</th>
              <th style={{ padding: '10px 12px' }}>Activation & Completion Trigger Rule</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, idx) => {
              const capColor = CAPTAIN_COLORS[s.captain] || CAPTAIN_COLORS.MERCH;
              const isAuto = s.source.includes('Auto');
              return (
                <tr
                  key={s.code}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: idx % 2 === 0 ? 'var(--bg)' : 'var(--bg2)',
                    transition: 'background 0.15s ease'
                  }}
                >
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text3)' }}>{s.seq}</td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#1A4F9C' }}>{s.code}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--text)' }}>{s.name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text2)' }}>{s.phase}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                      background: capColor.bg, color: capColor.text, border: `1px solid ${capColor.border}`,
                      display: 'inline-block'
                    }}>
                      {s.captain}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600,
                      background: isAuto ? 'rgba(16, 185, 129, 0.12)' : 'var(--bg3)',
                      color: isAuto ? '#10b981' : 'var(--text2)',
                      border: isAuto ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid var(--border)'
                    }}>
                      {s.source}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text2)', lineHeight: 1.45 }}>{s.trigger}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
